import { useState, useEffect, useRef } from "react";
import adminApi from "../api/adminAxios";

const inr = n => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);

const EMPTY_FORM = { name: "", price: "", description: "", category: "", image: null };

export default function ManageProducts() {
  const [products, setProducts]     = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState("");
  const [showModal, setShowModal]   = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [imagePreview, setImagePreview] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving]         = useState(false);
  const [deleteId, setDeleteId]     = useState(null);
  const fileInputRef                = useRef(null);

  useEffect(() => {
    Promise.all([
      adminApi.get("admin/products/"),
      adminApi.get("admin/categories/"),
    ]).then(([pRes, cRes]) => {
      setProducts(pRes.data);
      setCategories(cRes.data);
    }).catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const getCategoryName = cat => {
    if (!cat) return "—";
    if (typeof cat === "object") return cat.name || "—";
    const found = categories.find(c => c.id === cat);
    return found?.name || String(cat);
  };

  const filtered = products.filter(p => !search || p.name?.toLowerCase().includes(search.toLowerCase()));

  const openAdd = () => {
    setEditProduct(null);
    setForm(EMPTY_FORM);
    setImagePreview(null);
    setFormErrors({});
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditProduct(p);
    setForm({
      name:           p.name,
      price:          p.price,
      description:    p.description || "",
      category:       typeof p.category === "object" ? p.category?.id : p.category,
      image:          null,
    });
    setImagePreview(p.image || null);
    setFormErrors({});
    setShowModal(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm(f => ({ ...f, image: file }));
    setImagePreview(URL.createObjectURL(file));
  };

  const validateForm = () => {
    const errs = {};
    if (!form.name.trim())                                           errs.name           = "Product name is required.";
    if (!form.price)                                                 errs.price          = "Price is required.";
    else if (isNaN(form.price) || parseFloat(form.price) <= 0)      errs.price          = "Enter a valid price.";
    if (!form.category)                                              errs.category       = "Category is required.";
    return errs;
  };

  const handleSave = async () => {
    const errs = validateForm();
    if (Object.keys(errs).length) { setFormErrors(errs); return; }
    setSaving(true);

    const fd = new FormData();
    fd.append("name",           form.name);
    fd.append("price",          form.price);
    fd.append("description",    form.description);
    fd.append("category_id",    form.category);
    if (form.image instanceof File) fd.append("image", form.image);

    try {
      if (editProduct) {
        const res = await adminApi.patch(`admin/products/${editProduct.id}/`, fd);
        setProducts(prev => prev.map(p => p.id === editProduct.id ? res.data : p));
      } else {
        const res = await adminApi.post("admin/products/", fd);
        setProducts(prev => [res.data, ...prev]);
      }
      setShowModal(false);
    } catch (err) {
      const errData = err.response?.data;
      if (errData && typeof errData === "object") {
        const fieldErrors = {};
        Object.entries(errData).forEach(([k, v]) => {
          fieldErrors[k] = Array.isArray(v) ? v[0] : v;
        });
        setFormErrors(fieldErrors);
      } else {
        setFormErrors({ api: "Failed to save product. Please try again." });
      }
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await adminApi.delete(`admin/products/${id}/`);
      setProducts(prev => prev.filter(p => p.id !== id));
    } catch {}
    finally { setDeleteId(null); }
  };

  const inputStyle = (errKey) => ({
    width: "100%", padding: "10px 12px", borderRadius: "9px",
    border: `1.5px solid ${formErrors[errKey] ? "#e57373" : "#e8d5b8"}`,
    background: "#fffaf4", fontFamily: "Georgia, serif", fontSize: "13px",
    outline: "none", boxSizing: "border-box",
  });

  const labelStyle = {
    display: "block", fontFamily: "Georgia, serif", fontSize: "11px",
    fontWeight: "700", color: "#5a3a1a", marginBottom: "5px",
  };

  const errStyle = {
    fontFamily: "Georgia, serif", fontSize: "10px",
    color: "#c0392b", marginTop: "3px",
  };

  return (
    <div style={{ padding: "32px 36px", fontFamily: "Georgia, serif" }}>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "28px" }}>
        <div>
          <h1 style={{ fontFamily: "Georgia, serif", fontSize: "24px", fontWeight: "700", color: "#1a1a1a" }}>Manage Products</h1>
          <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", color: "#aaa", marginTop: "4px" }}>Review, update, and manage your scented candle inventory.</p>
        </div>
        <button onClick={openAdd}
          style={{ padding: "11px 22px", borderRadius: "11px", background: "linear-gradient(135deg, #e07b39, #c96a18)", color: "#fff", border: "none", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700", boxShadow: "0 4px 16px rgba(201,106,24,.28)" }}>
          + Add New Product
        </button>
      </div>

      {/* Search */}
      <div style={{ background: "#fff", borderRadius: "14px", padding: "16px 20px", border: "1px solid #f0ebe3", marginBottom: "16px" }}>
        <div style={{ position: "relative", maxWidth: "360px" }}>
          <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#bbb" }}>🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products..."
            style={{ width: "100%", padding: "8px 12px 8px 34px", borderRadius: "9px", border: "1.5px solid #e8d5b8", background: "#fffaf4", fontFamily: "Georgia, serif", fontSize: "12px", outline: "none", boxSizing: "border-box" }} />
        </div>
      </div>

      {/* Table */}
      <div style={{ background: "#fff", borderRadius: "14px", border: "1px solid #f0ebe3", overflow: "hidden" }}>
        <div style={{ display: "grid", gridTemplateColumns: "55px 64px 1fr 110px 80px 1fr 80px", gap: "8px", padding: "12px 20px", background: "#f7f2eb" }}>
          {["ID", "IMAGE", "PRODUCT NAME", "CATEGORY", "PRICE", "DESCRIPTION", "ACTIONS"].map(h => (
            <span key={h} style={{ fontFamily: "Georgia, serif", fontSize: "9px", fontWeight: "700", color: "#aaa", letterSpacing: "0.08em" }}>{h}</span>
          ))}
        </div>

        {loading ? (
          <p style={{ padding: "32px", color: "#aaa", fontFamily: "Georgia, serif", fontSize: "13px" }}>Loading products...</p>
        ) : filtered.length === 0 ? (
          <p style={{ padding: "32px", color: "#aaa", fontFamily: "Georgia, serif", fontSize: "13px", textAlign: "center" }}>No products found.</p>
        ) : (
          filtered.map(product => (
            <div key={product.id} style={{ display: "grid", gridTemplateColumns: "55px 64px 1fr 110px 80px 1fr 80px", gap: "8px", padding: "14px 20px", borderBottom: "1px solid #f7f2eb", alignItems: "center" }}>
              <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa" }}>#{product.id}</span>

              <div style={{ width: "44px", height: "44px", borderRadius: "9px", background: "#f0ebe3", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px", flexShrink: 0 }}>
                {product.image
                  ? <img src={product.image} alt={product.name} style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      onError={e => { e.target.style.display = "none"; e.target.parentNode.textContent = "🕯️"; }} />
                  : "🕯️"}
              </div>

              <span style={{ fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "600", color: "#1a1a1a" }}>{product.name}</span>

              <span style={{ padding: "3px 10px", borderRadius: "999px", background: "#f0ebe3", fontFamily: "Georgia, serif", fontSize: "11px", color: "#7a5c3a", display: "inline-block", width: "fit-content" }}>
                {getCategoryName(product.category)}
              </span>

              <span style={{ fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700", color: "#e07b39" }}>{inr(product.price)}</span>



              <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#888", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {product.description || "—"}
              </span>

              <div style={{ display: "flex", gap: "6px" }}>
                <button onClick={() => openEdit(product)} title="Edit"
                  style={{ background: "#fff4ec", border: "none", borderRadius: "6px", padding: "5px 8px", cursor: "pointer", fontSize: "13px" }}>✏️</button>
                <button onClick={() => setDeleteId(product.id)} title="Delete"
                  style={{ background: "#fff0f0", border: "none", borderRadius: "6px", padding: "5px 8px", cursor: "pointer", fontSize: "13px" }}>🗑️</button>
              </div>
            </div>
          ))
        )}
        {!loading && (
          <div style={{ padding: "12px 20px", background: "#faf7f2" }}>
            <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#aaa" }}>Showing {filtered.length} of {products.length} products</span>
          </div>
        )}
      </div>

      {/* ── Add / Edit Modal ─────────────────────────────────────────────── */}
      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px", overflowY: "auto" }}
          onClick={e => { if (e.target === e.currentTarget) setShowModal(false); }}>
          <div style={{ background: "#fff", borderRadius: "18px", padding: "28px", maxWidth: "500px", width: "100%", boxShadow: "0 16px 60px rgba(0,0,0,.15)", margin: "auto" }}>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "22px" }}>
              <h3 style={{ fontFamily: "Georgia, serif", fontSize: "16px", fontWeight: "700", color: "#1a1a1a" }}>
                {editProduct ? "Edit Product" : "Add New Product"}
              </h3>
              <button onClick={() => setShowModal(false)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px", color: "#aaa" }}>✕</button>
            </div>

            {formErrors.api && (
              <div style={{ background: "#fff0f0", border: "1px solid #f5c6cb", color: "#c0392b", borderRadius: "10px", padding: "10px 14px", marginBottom: "16px", fontFamily: "Georgia, serif", fontSize: "12px" }}>
                ⚠️ {formErrors.api}
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>

              <div>
                <label style={labelStyle}>Product Name *</label>
                <input value={form.name} onChange={e => { setForm(f => ({ ...f, name: e.target.value })); setFormErrors(fe => ({ ...fe, name: "" })); }}
                  placeholder="e.g. Lavender Bliss" style={inputStyle("name")} />
                {formErrors.name && <p style={errStyle}>⚠ {formErrors.name}</p>}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <label style={labelStyle}>Price (₹) *</label>
                  <input value={form.price} onChange={e => { setForm(f => ({ ...f, price: e.target.value })); setFormErrors(fe => ({ ...fe, price: "" })); }}
                    placeholder="e.g. 499" type="number" min="0" style={inputStyle("price")} />
                  {formErrors.price && <p style={errStyle}>⚠ {formErrors.price}</p>}
                </div>

              </div>

              <div>
                <label style={labelStyle}>Category *</label>
                <select value={form.category} onChange={e => { setForm(f => ({ ...f, category: e.target.value })); setFormErrors(fe => ({ ...fe, category: "" })); }}
                  style={inputStyle("category")}>
                  <option value="">Select category</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                {formErrors.category && <p style={errStyle}>⚠ {formErrors.category}</p>}
              </div>

              <div>
                <label style={labelStyle}>Description</label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  rows={3} placeholder="Short product description..."
                  style={{ ...inputStyle("description"), resize: "none" }} />
              </div>

              <div>
                <label style={labelStyle}>Product Image</label>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  style={{ border: "2px dashed #e8d5b8", borderRadius: "12px", padding: "16px", cursor: "pointer", background: "#fffaf4", textAlign: "center", transition: "border-color 0.2s", display: "flex", alignItems: "center", gap: "16px" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = "#e07b39"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = "#e8d5b8"}
                >
                  {imagePreview ? (
                    <>
                      <img src={imagePreview} alt="Preview"
                        style={{ width: "64px", height: "64px", objectFit: "cover", borderRadius: "9px", flexShrink: 0 }} />
                      <div style={{ textAlign: "left" }}>
                        <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", fontWeight: "600", color: "#1a1a1a", margin: 0 }}>
                          {form.image instanceof File ? form.image.name : "Current image"}
                        </p>
                        <p style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#e07b39", margin: "2px 0 0" }}>Click to change</p>
                      </div>
                    </>
                  ) : (
                    <div style={{ width: "100%", padding: "8px 0" }}>
                      <div style={{ fontSize: "28px", marginBottom: "6px" }}>📷</div>
                      <p style={{ fontFamily: "Georgia, serif", fontSize: "12px", color: "#aaa", margin: 0 }}>Click to upload product image</p>
                      <p style={{ fontFamily: "Georgia, serif", fontSize: "10px", color: "#ccc", margin: "3px 0 0" }}>JPG, PNG, WEBP — max 5MB</p>
                    </div>
                  )}
                </div>
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageChange} style={{ display: "none" }} />
                {imagePreview && (
                  <button onClick={() => { setForm(f => ({ ...f, image: null })); setImagePreview(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                    style={{ marginTop: "6px", background: "none", border: "none", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "11px", color: "#c0392b", padding: 0 }}>
                    ✕ Remove image
                  </button>
                )}
              </div>
            </div>

            <div style={{ display: "flex", gap: "10px", marginTop: "22px" }}>
              <button onClick={() => setShowModal(false)}
                style={{ flex: "0 0 auto", padding: "11px 20px", borderRadius: "10px", border: "1.5px solid #e8d5b8", background: "transparent", color: "#888", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "13px" }}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                style={{ flex: 1, padding: "11px", borderRadius: "10px", background: saving ? "#e0a870" : "linear-gradient(135deg, #e07b39, #c96a18)", color: "#fff", border: "none", cursor: saving ? "not-allowed" : "pointer", fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700", boxShadow: "0 4px 14px rgba(201,106,24,.25)" }}>
                {saving ? "Saving..." : editProduct ? "Save Changes" : "Add Product"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteId && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
          <div style={{ background: "#fff", borderRadius: "18px", padding: "28px", maxWidth: "360px", width: "100%", textAlign: "center", boxShadow: "0 16px 60px rgba(0,0,0,.15)" }}>
            <div style={{ fontSize: "40px", marginBottom: "12px" }}>🗑️</div>
            <h3 style={{ fontFamily: "Georgia, serif", fontSize: "16px", fontWeight: "700", color: "#1a1a1a", marginBottom: "8px" }}>Delete Product?</h3>
            <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", color: "#888", marginBottom: "24px" }}>This action cannot be undone. The product will be permanently removed.</p>
            <div style={{ display: "flex", gap: "10px" }}>
              <button onClick={() => setDeleteId(null)}
                style={{ flex: 1, padding: "11px", borderRadius: "10px", border: "1.5px solid #e8d5b8", background: "transparent", color: "#888", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "13px" }}>
                Keep It
              </button>
              <button onClick={() => handleDelete(deleteId)}
                style={{ flex: 1, padding: "11px", borderRadius: "10px", background: "#c0392b", color: "#fff", border: "none", cursor: "pointer", fontFamily: "Georgia, serif", fontSize: "13px", fontWeight: "700" }}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}